import { produce } from 'immer';
import { LOCATION_CHANGE, LocationChangeAction } from 'react-router-redux';

import { getSheetKey } from './sheetSelectors';

import { SheetAction, SheetActionTypes } from './sheetActionTypes';
import { SheetFlowId, SheetReducerState, SheetState } from './sheetTypes';

const INITIAL_STATE: SheetReducerState = {};
type SheetEntry = [string, SheetState];
type SheetOpenAction = Extract<SheetAction, { type: SheetActionTypes.SHEET_OPEN }>;

/**
 * Create the next active sheet state for an open action.
 */
const getNextSheetState = (action: SheetOpenAction, previousSheetState?: SheetState): SheetState => {
    const { sheetId, instanceId, config, options } = action;

    return {
        active: true,
        sheetId,
        instanceId,
        activeSnapPoint: 0,
        data: options?.data,
        previousSheetState,
        config,
    };
};

/**
 * Clone a flow sheet into the shape used when restoring it later.
 */
const getRestorableSheetState = (sheetState: SheetState): SheetState => ({
    ...sheetState,
    active: true,
    activeSnapPoint: 0,
    pendingSheetState: undefined,
});

/**
 * A sheet handoff should wait until the current sheet has fully unmounted
 * before the next sheet becomes active.
 */
const deferSheetHandoff = (
    draft: SheetReducerState,
    sheetKey: string,
    sheetState: SheetState,
    nextSheetState?: SheetState,
) => {
    draft[sheetKey] = {
        ...sheetState,
        active: false,
        activeSnapPoint: 0,
        pendingSheetState: nextSheetState,
    };
};

/**
 * Build the sheet state that should be restored after the current sheet closes.
 */
const getPreviousSheetStateToRestore = (
    previousSheetState?: SheetState,
    skipPreviousSheetRestore?: boolean,
): SheetState | undefined => {
    if (!previousSheetState || skipPreviousSheetRestore) return;

    return getRestorableSheetState(previousSheetState);
};

/**
 * Find the current sheet participating in a flow, excluding the current sheet if provided.
 */
const getExistingFlowSheetEntry = (
    state: SheetReducerState,
    flow?: SheetFlowId,
    excludedSheetKey?: string,
): SheetEntry | null => {
    if (!flow) return null;

    const sheetKeys = Object.keys(state);
    if (sheetKeys.length === 0) return null;

    for (const sheetKey of sheetKeys) {
        if (sheetKey === excludedSheetKey) continue;

        const sheetState = state[sheetKey];
        if (!sheetState) continue;
        if (sheetState.config?.flow !== flow) continue;

        return [sheetKey, sheetState];
    }

    return null;
};

/**
 * Open a sheet, deferring the handoff when another sheet in the same flow is already present.
 */
const handleSheetOpen = (draft: SheetReducerState, state: SheetReducerState, action: SheetOpenAction) => {
    const { sheetId, instanceId, options, config } = action;

    const sheetKey = getSheetKey(sheetId, instanceId);
    if (state[sheetKey]?.active === true) return;

    const existingFlowSheetEntry = getExistingFlowSheetEntry(state, config?.flow, sheetKey);
    const previousSheetState =
        options?.previousSheetState ||
        (existingFlowSheetEntry ? getRestorableSheetState(existingFlowSheetEntry[1]) : undefined);
    const nextSheetState = getNextSheetState(action, previousSheetState);

    if (!existingFlowSheetEntry) {
        draft[sheetKey] = nextSheetState;
        return;
    }

    // When another sheet in the flow is already present, keep that sheet mounted
    // until its close transition completes, then activate the next sheet.
    const [existingFlowSheetKey, existingFlowSheetState] = existingFlowSheetEntry;
    deferSheetHandoff(draft, existingFlowSheetKey, existingFlowSheetState, nextSheetState);
};

/**
 * Close a sheet, deferring any restore until the close transition completes.
 */
const handleSheetClose = (
    draft: SheetReducerState,
    state: SheetReducerState,
    action: Extract<SheetAction, { type: SheetActionTypes.SHEET_CLOSE }>,
) => {
    const { sheetId, instanceId, options } = action;

    const sheetKey = getSheetKey(sheetId, instanceId);
    const sheetState = state[sheetKey];
    if (!sheetState) return;

    const previousSheetState = getPreviousSheetStateToRestore(
        sheetState.previousSheetState,
        options?.skipPreviousSheetRestore,
    );

    if (previousSheetState) {
        // Any restore follows the same deferred handoff:
        // the current sheet closes first, then the next sheet is activated.
        deferSheetHandoff(draft, sheetKey, sheetState, previousSheetState);
        return;
    }

    delete draft[sheetKey];
};

/**
 * Finalise a deferred sheet handoff once the closing sheet has fully unmounted.
 */
const handleSheetCloseTransitionComplete = (
    draft: SheetReducerState,
    state: SheetReducerState,
    action: Extract<SheetAction, { type: SheetActionTypes.SHEET_CLOSE_TRANSITION_DID_COMPLETE }>,
) => {
    const { sheetKey } = action;
    const sheetState = state[sheetKey];
    if (!sheetState) return;

    const pendingSheetState = state[sheetKey]?.pendingSheetState;

    // Remove the closing sheet first so the next flow sheet appears only after the
    // previous one has fully unmounted.
    delete draft[sheetKey];

    if (!pendingSheetState) return;

    const pendingSheetKey = getSheetKey(pendingSheetState.sheetId, pendingSheetState.instanceId);
    draft[pendingSheetKey] = pendingSheetState;
};

export default (state = INITIAL_STATE, action: SheetAction | LocationChangeAction) => {
    switch (action.type) {
        case SheetActionTypes.SHEET_OPEN:
            return produce(state, (draft) => handleSheetOpen(draft, state, action));
        case SheetActionTypes.SHEET_CLOSE:
            return produce(state, (draft) => handleSheetClose(draft, state, action));
        case SheetActionTypes.SHEET_CLOSE_TRANSITION_DID_COMPLETE:
            return produce(state, (draft) => handleSheetCloseTransitionComplete(draft, state, action));
        case SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT:
            return produce(state, (draft) => {
                const { sheetId, instanceId } = action;

                const sheetKey = getSheetKey(sheetId, instanceId);
                const sheet = draft[sheetKey];
                if (!sheet) return;

                sheet.activeSnapPoint = action.activeSnapPoint;
            });
        case LOCATION_CHANGE:
            return produce(state, (draft) => {
                Object.keys(draft).forEach((sheetKey) => {
                    const sheet = draft[sheetKey];
                    if (!sheet?.config?.stayActiveOnLocationChange) {
                        delete draft[sheetKey];
                    }
                });
            });
        default:
            return state;
    }
};
